import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom } from 'jotai';
import { Typography, IconButton } from '@mui/material';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import useAPI from '../../../api';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import { CenteredFlexBox } from '../../../Components/FlexBox';
import CaptionValue from '../../../Components/CaptionValue/CaptionValue';
import ContactsManagerWindow from '../Shared/ContactsManagerWindow';
import validateEmail from '../../../Helpers/Validation/validateEmail';

const EmailContacts = function EmailContacts({
  contact,
  changePhoneOrMail,
  selectedEmailAddress,
  handleChangeSelectedEmailAddress,
}) {
  const intl = useIntl();
  const API = useAPI();

  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const [emailAddresses, setEmailAddresses] = useState([]);
  const [contactWindow, setContactWindow] = useState(null);
  const [emailTypes, setEmailTypes] = useState([]);

  useEffect(() => {
    if (contact && contact?.contactEmailAddresses) {
      setEmailAddresses(contact.contactEmailAddresses);
    }
  }, [contact]);

  useEffect(() => {
    API.getEmailTypes(
      (res) => {
        const emailTypesOptions = res.map((item) => ({
          label: item.emailType,
          value: item.id,
          isPriority: item.isPriority,
        }));
        setEmailTypes(emailTypesOptions);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.EMAIL_TYPE_LOADING_ERROR,
          severity: 'error',
        });
      }
    );
  }, []);

  /**Contact emailAddresses**/
  //recompute the selected email from a list: active priority first, then any active, else none
  const refreshSelectedEmailAddress = (emails) => {
    const activeEmails = emails?.filter(({ active }) => active) || [];
    handleChangeSelectedEmailAddress(
      (activeEmails.find(({ priority }) => priority) || activeEmails[0])
        ?.email || null
    );
  };

  const handleChangeSelectedEmail = (emailAddress) => {
    API.setEmailAsDefault(
      contact?.id,
      emailAddress,
      1, //Priority,
      () => {
        handleChangeSelectedEmailAddress(emailAddress);
      },
      () => {}
    );
  };

  const handleAddNewEmailAddress = (
    newAddress,
    newEmailType,
    priority,
    isPrincipal
  ) => {
    if (validateEmail(newAddress)) {
      API.addNewEmailAddress(
        contact?.id,
        newAddress,
        newEmailType.value, //emailType
        priority,
        () => {
          changePhoneOrMail(
            {
              email: newAddress,
              emailTypeId: newEmailType.value,
              emailType: newEmailType.label,
              priority,
              isPrincipal,
            },
            false,
            false,
            true
          );
          //a new active email may become the one that should be selected
          refreshSelectedEmailAddress([
            ...emailAddresses,
            { email: newAddress, priority, active: true },
          ]);
        },
        () => {}
      );
    }
  };

  const handleChangeEmailStatus = (emailAddress, isPriority) => {
    API.changeEmailStatus(
      contact?.id,
      emailAddress,
      () => {
        const copyArray = [...emailAddresses];
        let item = copyArray.find(({ email }) => email === emailAddress);
        const index = copyArray.findIndex(
          ({ email }) => email === emailAddress
        );

        item = Object.assign(item, { active: !item.active });
        copyArray.splice(index, 1, item);

        setEmailAddresses(copyArray);
        //activate/deactivate may change which email should be selected
        refreshSelectedEmailAddress(copyArray);
        if (isPriority) {
          changePhoneOrMail(emailAddress, false, false, false);
        }
      },
      () => {}
    );
  };
  /******/

  const handleOpenEmailsMenu = (e) => {
    setContactWindow(e.currentTarget);
  };
  const handleCloseEmailsMenu = () => {
    setContactWindow(null);
  };
  const openMenu = Boolean(contactWindow);
  return (
    <>
      <ContactsManagerWindow
        listTitle={`${intl.messages.EMAIL_ADDRESSES}:`}
        title={intl.messages.EMAIL_ADDRESS}
        anchorEl={contactWindow}
        open={openMenu}
        close={handleCloseEmailsMenu}
        selectedItem={selectedEmailAddress}
        onChangeSelection={(address) =>
          selectedEmailAddress !== address && handleChangeSelectedEmail(address)
        }
        handleChangeContactStatus={handleChangeEmailStatus}
        addNewContact={handleAddNewEmailAddress}
        sourceArray={emailAddresses?.map(
          ({ email, emailType, emailTypeId, active, emailTypeModel }) => {
            const isPriority = emailTypeModel?.isPriority ?? false;
            return {
              value: email,
              type: emailType,
              typeId: emailTypeId,
              active,
              isPriority,
            };
          }
        )}
        valueTypesArray={emailTypes}
        errorMessage={intl.messages.INVALID_EMAIL_ADDRESS}
      />
      <CaptionValue
        width="100%"
        caption={intl.messages.EMAIL_ADDRESS}
        captionStyle={{ color: '#fff' }}
        padding="0px 0px 18px 0px"
        value={
          <CenteredFlexBox>
            <Typography
              noWrap
              variant="body1"
              sx={{
                color: "white.main",
                fontSize: "24px"
              }}>
              {selectedEmailAddress || intl.messages.UNAVAILABLE}
            </Typography>
            <IconButton size="small" onClick={handleOpenEmailsMenu}>
              <ChangeCircleOutlinedIcon sx={{ color: '#fff' }} />
            </IconButton>
          </CenteredFlexBox>
        }
      />
    </>
  );
};

export default EmailContacts;
